/**
 * Shared adapter inputs.
 *
 * Every per-kind adapter takes (entity, ctx) and returns a
 * `RenderableEntity`. The context carries the bits that need
 * pipeline-level resolution — layer color table, parent INSERT colour
 * (BYBLOCK chain), lineweight default, draw order — so adapters stay
 * focused on geometry.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type {
  BlockDefinition,
  DimensionStyle,
  DrawingEntity,
} from "@dxf-ai-editor/drawing-model";

import { readNumber, readPositiveNumber } from "../../scene/renderHelpers";
import {
  resolveColorHex,
  resolveColorHexFromEntity,
  type LayerColorMap,
} from "../rendering/colorResolution";
import { foregroundHexForRenderedSpace } from "../rendering/layoutForeground";
import type { LayerLinetypeMap } from "../rendering/linetypeResolve";
import type { RenderableEntity, SceneGeometry, Vec3 } from "../types";

/**
 * DEFUSE — shared legacy center-read preamble for the round-geometry
 * adapters (ARC / CIRCLE / ELLIPSE). Each reads the same `Center X/Y/Z`
 * keys from the `EntityPreview.properties` string bag into a `Vec3`; this
 * is the single domain truth for "where the legacy bag stores the centre".
 *
 * Behaviour-preserving extraction of an identical 4-line block the jscpd
 * gate flagged as an incidental clone across arcToRenderable /
 * circleToRenderable / ellipseToRenderable. Legacy-only (string-bag path);
 * the typed adapters resolve the centre off the canonical model directly.
 */
export function readCenterRaw(properties: Record<string, string>): Vec3 {
  return {
    x: readNumber(properties, "Center X"),
    y: readNumber(properties, "Center Y"),
    z: readNumber(properties, "Center Z"),
  };
}

/** Common placement primitives both text-like legacy adapters extract. */
export interface TextBagPlacement {
  insRaw: Vec3;
  height: number;
  rotation: number;
  widthFactor: number;
}

/**
 * DEFUSE S4 — typed counterpart of `readPositiveNumber` (`scene/renderHelpers`).
 * The legacy adapters read positive-or-default fields off the string bag via
 * `readPositiveNumber(props, key, fallback)` = `isFinite(n) && n > 0 ? n :
 * fallback`. The typed adapters read those same fields off the model, where a
 * `z.number().default(d)` / `.nonnegative()` schema still admits 0 or a negative
 * value, so they must apply the IDENTICAL positive-or-default clamp — otherwise
 * a 0/negative model field (e.g. MTEXT `lineSpacingFactor`, TEXT `height` /
 * `widthFactor`) would pass through typed while the legacy path falls back,
 * diverging the golden-parity output. One truth so the clamp cannot drift.
 */
export function positiveOr(value: number | undefined, fallback: number): number {
  return value !== undefined && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

/**
 * DEFUSE S10 (@codex #431 P2 — Z preserve for text-like block children).
 * Read the OPTIONAL z off a nominally-2D model point (`drawingPointSchema`).
 *
 * TEXT / MTEXT `insertionPoint` is `{x, y}` on the schema, so a top-level
 * entity never carries z (zod strips unknown keys on parse) and this returns
 * the legacy-parity `0`. The typed block flatten, however, hands the adapters
 * a TRANSFORMED clone whose insertion went through the block z-collapse
 * (`((z ?? 0) − basePoint.z)·scaleZ + insertion.z`, `blockTransform.ts`) — a
 * real z that must reach the text geometry like it reaches the Line / Circle /
 * Spline siblings, instead of being discarded by a hard-coded `z: 0`. One
 * truth for both text-like typed adapters so the read cannot drift.
 */
export function optionalPointZ(p: { x: number; y: number }): number {
  const z = (p as { z?: unknown }).z;
  return typeof z === "number" && Number.isFinite(z) ? z : 0;
}

/**
 * DEFUSE S4 — shared legacy placement-read preamble for the text-like
 * adapters (TEXT / MTEXT). Both read the same `Insertion X/Y/Z` + `Height`
 * + `Rotation` + `Width factor` keys from the `EntityPreview.properties`
 * string bag with IDENTICAL defaults (height 2.5, width factor 1) — this is
 * the single domain truth for "where the legacy bag stores a text entity's
 * placement". Behaviour-preserving extraction of the block the jscpd gate
 * flagged as a clone across textToRenderable / mtextToRenderable. Legacy-only
 * (string-bag path); the typed adapters read the placement off the canonical
 * model directly. (MTEXT carries no glyph width factor in the bag, so its
 * `readPositiveNumber("Width factor", 1)` returns the default 1 — identical
 * call, the value just differs; safe to share.)
 */
export function readTextBagPlacement(
  properties: Record<string, string>,
): TextBagPlacement {
  return {
    insRaw: {
      x: readNumber(properties, "Insertion X"),
      y: readNumber(properties, "Insertion Y"),
      z: readNumber(properties, "Insertion Z"),
    },
    height: readPositiveNumber(properties, "Height", 2.5),
    rotation: readNumber(properties, "Rotation"),
    widthFactor: readPositiveNumber(properties, "Width factor", 1),
  };
}

/** Camera + drawing parameters every adapter needs. */
export interface AdapterContext {
  /** Layer-name → color record map (lower-cased keys). */
  layerColors: LayerColorMap;
  /**
   * Resolved colour of the parent INSERT walked through during BYBLOCK
   * resolution. `null` at root scope (entity not inside a block).
   */
  blockColor?: string | null;
  /**
   * FIX-2 (Codex P2) — resolved linetype NAME of the parent INSERT, threaded
   * during block flatten. A block child whose linetype is the `ByBlock`
   * sentinel inherits THIS name (mirrors the `blockColor` BYBLOCK chain).
   * `null` / undefined at root scope (entity not inside a block) → a residual
   * `ByBlock` then degrades to `Continuous`, matching the colour path.
   */
  blockLinetype?: string | null;
  /**
   * PR-LT3 — product of every enclosing INSERT's linetype scale (DXF group 48).
   * A block child's effective linetypeScale = its own × this. Undefined/absent
   * → 1 (root scope).
   */
  blockLinetypeScale?: number;
  /**
   * FIX-2 (Codex P2) — per-layer LTYPE map (lower-cased layer name → linetype
   * NAME). Used at flatten time to resolve a parent INSERT's OWN linetype when
   * the INSERT itself is `ByLayer` (so a ByBlock child under a ByLayer INSERT
   * inherits the INSERT's layer linetype). Optional — absent → a ByLayer
   * INSERT contributes `Continuous` to its ByBlock children.
   */
  layerLinetypes?: LayerLinetypeMap;
  /**
   * Camera zoom factor used by tessellation LOD. Pass `1` when adapter
   * is called from a unit test or when zoom is unknown — adapters fall
   * back to the minimum segment count in that case.
   */
  cameraZoom: number;
  /**
   * 0-based draw order index inside the active space, populated by the
   * pipeline. Used to fill `RenderableEntity.zOrder` for the DRAWORDER
   * sort in `batchedLayer.ts`.
   */
  zOrderIndex: number;
  /**
   * Lane H / S8 — the document's DIMSTYLE table, name-keyed, for the typed
   * `dimensionFromEntity` adapter's DIMVAR resolution (mirrors the legacy
   * bag producer's `dimStylesByName` — see
   * `adapters/geom/dimensionGeometry.ts`'s `resolveDimensionStyleRow`
   * JSDoc for why this must be the STYLE TABLE, not `entity.styleOverrides`).
   * Optional / undefined outside the 3 live lift call sites (unit tests,
   * PDF converters, the draft-preview channel) — an absent table falls back
   * to hardcoded DIMVAR defaults, same as an empty `Map`.
   */
  dimensionStyles?: ReadonlyMap<string, DimensionStyle>;
  /**
   * Lane H / S9 — block definitions keyed by name, threaded to the typed
   * `multileaderFromEntity` adapter for block-content flatten
   * (`flattenBlockReference`) — the SAME table the typed INSERT flatten
   * (`typed/blockFromEntity.ts`) receives as an explicit parameter at its
   * OWN call sites. Optional; absent/empty → a block-content MLEADER
   * flattens to zero primitives (mirrors the legacy `MultileaderAdapterContext`
   * default-`{}` fallback).
   */
  blocks?: Record<string, BlockDefinition>;
  /**
   * Lane H / S9 — active annotation scale factor (CANNOSCALE → numeric,
   * resolved from the annotation-scale catalogue), threaded to the typed
   * `multileaderFromEntity` adapter's annotative-context picking. `null` =
   * no scale active / non-annotative drawing; undefined outside the live
   * lift call sites.
   */
  annotationScale?: number | null;
  /**
   * S10d — active CANNOSCALE **name** (e.g. "1:100"), threaded to the typed
   * TEXT / MTEXT / HATCH / INSERT / ATTDEF adapters for per-scale annotative
   * representation resolution (`resolveTypedAnnotation` → `resolveCannoscaleContext`,
   * the SAME resolver the legacy SVG path uses). Distinct from `annotationScale`
   * (the numeric factor for MULTILEADER): annotative contexts are matched by
   * `scaleName`, so the NAME is required here. `null` = no scale active /
   * non-annotative drawing; `undefined` outside the live lift call sites →
   * treated as "no active scale" (no per-scale override, entities stay visible).
   */
  activeAnnotationScale?: string | null;
  /**
   * S10d — the document header's ANNOALLVISIBLE flag ("show annotations at all
   * scales"). Threaded to the annotative adapters so an entity with NO
   * representation for the active scale is hidden when `false`, and kept (at
   * its master fields) when `true`. AutoCAD's default is 1 (true); `undefined`
   * here → defaults to `true` at the resolver, so an unset flag never hides
   * anything (safe golden-parity default).
   */
  annoAllVisible?: boolean;
  /**
   * The drawing's `$FILLMODE` HEADER variable, read off the typed document
   * field `DrawingDocument.fillmode` and threaded here so the shell can stamp
   * it onto every `RenderableEntity` (see that field's doc for the semantics
   * and for why ABSENT means the Autodesk INITIAL VALUE 1).
   *
   * Document-level, not per-entity — it rides the context for the same reason
   * `annoAllVisible` and `activeAnnotationScale` do: the consumers that need
   * it (`batchedLayer`'s band stream, the band fill mesh, the pointer pick and
   * window select) each reach the entity and none of them reaches the store.
   * A block child inherits it through the usual `{ ...ctx }` spread, so a wide
   * polyline inside an INSERT obeys the same drawing switch as one at top
   * level.
   */
  fillmode?: number;
  /**
   * # Which space this pass is rendering (NOT a colour)
   *
   * The identity of the space the user is in — `DrawingDocument.activeSpaceId`
   * and that space's `DrawingSpace["kind"]`, stamped once per pass by
   * `scene/buildAdapterCtxBase.ts`.
   *
   * The context deliberately carries NO foreground colour. ACI 7 is the
   * drawing-area CONTRAST, so the ink and the background it contrasts with are
   * ONE pair (`rendering/layoutForeground.ts`); a context field holding a
   * ready-made foreground let a caller name one without the matching
   * background, which is exactly how a layout pass came to paint model-space
   * geometry paper-BLACK onto the dark viewport background it is really drawn
   * on (@codex round 15 P1). The space identity is what a pass actually knows;
   * the pair is derived from it, per entity, by {@link entityForegroundHex}.
   *
   * Both optional: omitted → every entity reads as model space → the
   * historical `#ffffff`, so unit tests / the PDF converters / the
   * draft-preview channel stay byte-identical. A block child inherits them
   * through the usual `{ ...ctx }` spread (`blockReferenceChildContext`), so
   * the title block inside an INSERT obeys the same sheet rule its top-level
   * siblings do.
   */
  activeSpaceId?: string;
  /** `DrawingSpace["kind"]` of {@link activeSpaceId} — see its doc. */
  activeSpaceKind?: string;
}

/**
 * The drawing-area foreground THIS entity's ACI 7 (and every BYLAYER /
 * BYBLOCK walk that lands on it, and every unresolvable colour) paints in —
 * `#ffffff` on the dark model background, `#000000` on a layout's white sheet.
 *
 * Per ENTITY, not per pass: a paper layout renders its own sheet AND model
 * space in one pass (the VIEWPORT scissor passes mask objects that already
 * exist in the shared scene), and those two halves sit on two different
 * backgrounds. `rendering/layoutForeground.ts` owns the rule, the ezdxf
 * reference and both measured bugs; this is the one place the adapter layer
 * asks it.
 */
export function entityForegroundHex(
  entity: { spaceId: string },
  ctx: AdapterContext,
): string {
  return foregroundHexForRenderedSpace({
    entitySpaceId: entity.spaceId,
    activeSpaceId: ctx.activeSpaceId,
    activeSpaceKind: ctx.activeSpaceKind,
  });
}

/**
 * Build the common scaffolding part of every `RenderableEntity` (id,
 * layer, color, lineweight, zOrder, bounds + the supplied geometry).
 *
 * Adapters call this after computing their geometry-specific bbox.
 *
 * DEFUSE S2 — two overloads coexist during the string-bag → typed
 * migration:
 *   1. legacy `EntityPreview` (reads the `properties` string bag);
 *   2. typed `DrawingEntity` (reads `layer`/`color`/`linetype`/`lineweight`/
 *      `linetypeScale` directly off `DrawingEntityBase`).
 * Both produce the SAME `RenderableEntity` shell so the S3 golden-parity
 * harness can compare them. The typed overload is NOT wired into any render
 * path yet (category B); the legacy path is unchanged.
 */
export function buildRenderableShell(
  entity: EntityPreview,
  ctx: AdapterContext,
  geometry: SceneGeometry,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): RenderableEntity;
export function buildRenderableShell(
  entity: DrawingEntity,
  ctx: AdapterContext,
  geometry: SceneGeometry,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): RenderableEntity;
// Union overload — for shared assemble helpers (e.g. `assembleMTextRenderable`)
// that accept either path's entity and let the runtime `"properties" in entity`
// discrimination route it. Specific-type call sites still bind to the narrower
// overloads above (TS picks the first match); the impl already handles both.
export function buildRenderableShell(
  entity: EntityPreview | DrawingEntity,
  ctx: AdapterContext,
  geometry: SceneGeometry,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): RenderableEntity;
export function buildRenderableShell(
  entity: EntityPreview | DrawingEntity,
  ctx: AdapterContext,
  geometry: SceneGeometry,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): RenderableEntity {
  // Discriminate by the presence of the `properties` string bag: only the
  // derived `EntityPreview` carries it; the canonical `DrawingEntity` does
  // not. This keeps the legacy path byte-identical while routing typed
  // entities to the typed reader.
  if (!("properties" in entity)) {
    return buildRenderableShellTyped(entity, ctx, geometry, bounds);
  }
  const layer = entity.properties.Layer ?? entity.properties.layer ?? "0";
  const colorHex = resolveColorHex(
    entity.properties.Color ?? entity.properties.color,
    layer,
    ctx.layerColors,
    ctx.blockColor ?? null,
    entityForegroundHex(entity, ctx),
  );
  const lineweightWorld = parseLineweight(
    entity.properties.Lineweight ?? entity.properties.lineweight,
  );
  // PR-LT3 — propagate the DXF group-6 linetype NAME. Mirrors how
  // `Layer` / `Color` are read from the property bag (typed `Linetype` key,
  // lowercase `linetype` fallback). The default `"ByLayer"` matches AutoCAD's
  // CELTYPE default + the `blockRender.tsx` convention; the sentinel is
  // resolved to a concrete pattern later, at `batchedLayer` bucket-key time.
  //
  // FIX-2 (Codex P2) — a `ByBlock` child substitutes the parent INSERT's
  // resolved linetype (`ctx.blockLinetype`), mirroring the `blockColor`
  // BYBLOCK chain in `resolveColorHex`. At root scope (`blockLinetype` null)
  // a residual `ByBlock` degrades to `Continuous`.
  const linetype = resolveLinetypeName(
    entity.properties.Linetype ?? entity.properties.linetype,
    ctx.blockLinetype ?? null,
  );
  // PR-LT3 variant A (A2) — per-entity linetype scale (CELTSCALE, DXF group
  // 48). The preview property bag surfaces it under the Title-Case key
  // `"Linetype Scale"` (command-engine `previewBuilders.ts`), mirroring the
  // `common.linetypeScale` model field. Parsed to a number; absent / blank /
  // non-finite → 1 (AutoCAD's per-object default). Carried to the GPU as the
  // per-instance `instanceLtScale` attribute (NOT part of the bucket key).
  //
  // PR-LT3 (Codex P2 follow-up) — an enclosing INSERT's OWN group-48 scale
  // MULTIPLIES the linetype scale of the block geometry it contains (AutoCAD
  // semantics). Through a nested chain the scales multiply: a leaf's EFFECTIVE
  // scale = its own group-48 × product of every enclosing INSERT's group-48
  // (threaded as `ctx.blockLinetypeScale`, mirroring the `blockLinetype`
  // BYBLOCK chain). Own scale keeps `parseLinetypeScale`'s clamp (missing /
  // non-positive → 1); the multiply only applies the inherited chain (→ 1 at
  // root scope, so a standalone entity is unchanged).
  const ownLinetypeScale = parseLinetypeScale(
    entity.properties["Linetype Scale"] ?? entity.properties["Linetype scale"],
  );
  return assembleRenderableShell(entity, ctx, geometry, bounds, {
    layer,
    colorHex,
    lineweightWorld,
    linetype,
    ownLinetypeScale,
  });
}

/**
 * Lane H — resolve JUST the shell colour (BYLAYER/BYBLOCK chain) via a
 * throwaway `buildRenderableShell` call with degenerate geometry. Dimension
 * and Multileader (both legacy bag AND typed adapters) need the resolved
 * colour BEFORE they can build their real primitives (every primitive
 * carries `colorHex`), so all four call sites built an identical "temp
 * shell, discard the geometry, keep `.colorHex`" block — jscpd dup-gate
 * extraction, works for either `buildRenderableShell` overload via the
 * union entity type. The `"point"` geometry literal is never read; any
 * valid `SceneGeometry` variant would do.
 */
export function resolveShellColorHex(
  entity: EntityPreview | DrawingEntity,
  ctx: AdapterContext,
): string {
  const shell = buildRenderableShell(
    entity,
    ctx,
    { kind: "point", position: { x: 0, y: 0, z: 0 }, pdmode: 0, pdsize: 0 },
    { minX: 0, minY: 0, maxX: 0, maxY: 0 },
  );
  return shell.colorHex;
}

/**
 * DEFUSE S2 — shared final assembly for both `buildRenderableShell` overloads.
 * Takes the already-resolved primitive shell fields (resolved DIFFERENTLY by
 * each path — string-bag vs typed model) and folds in the inherited
 * block-linetype-scale chain + draw order, producing the identical
 * `RenderableEntity` shape. Extracted so the legacy and typed readers share
 * ONE assembly truth (any future shell-field addition changes both paths in
 * one place) rather than duplicating the object literal + scale multiply.
 */
function assembleRenderableShell(
  entity: { id: string; spaceId: string; type: RenderableEntity["type"] },
  ctx: AdapterContext,
  geometry: SceneGeometry,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
  resolved: {
    layer: string;
    colorHex: string;
    lineweightWorld: number;
    linetype: string;
    /** Per-entity own linetype scale (already clamped to a positive number). */
    ownLinetypeScale: number;
  },
): RenderableEntity {
  return {
    id: entity.id,
    spaceId: entity.spaceId,
    type: entity.type,
    bounds,
    layer: resolved.layer,
    colorHex: resolved.colorHex,
    lineweightWorld: resolved.lineweightWorld,
    linetype: resolved.linetype,
    linetypeScale: resolved.ownLinetypeScale * (ctx.blockLinetypeScale ?? 1),
    zOrder: ctx.zOrderIndex,
    // `$FILLMODE` — stamped ONLY when the drawing declares the NON-default
    // state. Absent means 1 (the Autodesk initial value) everywhere that reads
    // it, so a default drawing produces byte-identical shells to the ones it
    // produced before this field existed, and a 685k-entity load allocates no
    // extra property slot. `fillModePaintsSolid` is the one reader of the
    // absent-means-1 rule.
    ...(ctx.fillmode === 0 ? { fillmode: 0 } : {}),
    geometry,
  };
}

/**
 * DEFUSE S2 — typed implementation of `buildRenderableShell`. Reads the
 * shell fields directly off the canonical `DrawingEntity` (`DrawingEntityBase`
 * in `model.ts`) instead of the `EntityPreview.properties` string bag:
 *   - `entity.layer`         (was `properties.Layer`)
 *   - `entity.color`         (was `properties.Color`)   — via typed colour reader
 *   - `entity.lineweight`    (was `properties.Lineweight`)
 *   - `entity.linetype`      (was `properties.Linetype`)
 *   - `entity.linetypeScale` (was `properties["Linetype Scale"]`, a number now)
 *
 * Resolution logic (colour chain, lineweight 0.01-mm heuristic, ByLayer/
 * ByBlock linetype, nested linetype-scale multiply) is IDENTICAL to the
 * legacy path — only the SOURCE of the raw values changes — so the produced
 * shell is byte-for-byte the same the legacy bag path would produce.
 *
 * NOT exported and NOT wired anywhere yet; reached only through the typed
 * `buildRenderableShell` overload above (category B).
 */
function buildRenderableShellTyped(
  entity: DrawingEntity,
  ctx: AdapterContext,
  geometry: SceneGeometry,
  bounds: { minX: number; minY: number; maxX: number; maxY: number },
): RenderableEntity {
  const layer = entity.layer ?? "0";
  const colorHex = resolveColorHexFromEntity(
    entity,
    ctx.layerColors,
    ctx.blockColor ?? null,
    entityForegroundHex(entity, ctx),
  );
  const lineweightWorld = parseLineweight(entity.lineweight);
  const linetype = resolveLinetypeName(entity.linetype, ctx.blockLinetype ?? null);
  // Typed `linetypeScale` is already a number (DXF group 48); apply the SAME
  // clamp the string parser applies (missing / non-finite / non-positive → 1)
  // so the per-object default + degenerate-scale guard match the legacy path,
  // then fold in the inherited block-scale chain identically.
  const ownLinetypeScale =
    entity.linetypeScale !== undefined &&
    Number.isFinite(entity.linetypeScale) &&
    entity.linetypeScale > 0
      ? entity.linetypeScale
      : 1;
  return assembleRenderableShell(entity, ctx, geometry, bounds, {
    layer,
    colorHex,
    lineweightWorld,
    linetype,
    ownLinetypeScale,
  });
}

/**
 * PR-LT3 variant A (A2) — parse the per-entity linetype scale (CELTSCALE, DXF
 * group 48) from its preview property-bag string. Returns `1` for missing,
 * blank, non-numeric, or non-positive input (AutoCAD's per-object default; a
 * non-positive CELTSCALE is degenerate and must not collapse the dash period).
 *
 * Exported (PR-LT3) so the block-flatten path can reuse the SAME clamp when
 * reading an enclosing INSERT's OWN group-48 for the nested-scale chain, rather
 * than duplicating the parser (a jscpd dup gate would flag a copy).
 */
export function parseLinetypeScale(raw: string | undefined): number {
  if (raw === undefined || raw === null) return 1;
  const trimmed = String(raw).trim();
  if (trimmed === "") return 1;
  const parsed = parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

/**
 * Normalise the raw `Linetype` property-bag value into the name carried on
 * `RenderableEntity.linetype`. An empty / missing value defaults to the
 * `"ByLayer"` sentinel (AutoCAD CELTYPE default). Any other value is passed
 * through verbatim (trimmed) so a literal LTYPE name — including custom
 * names — survives to the bucket-key resolver untouched.
 *
 * FIX-2 (Codex P2) — `ByBlock` resolution. When `blockLinetype` is provided
 * (the entity is a block child, flattened under an INSERT whose linetype was
 * already resolved), a `ByBlock` raw value substitutes that parent INSERT
 * linetype — mirroring how `resolveColorHex` substitutes `blockColor` for a
 * BYBLOCK colour. At root scope (`blockLinetype` null) a `ByBlock` value
 * degrades to `Continuous` (no enclosing block), matching the colour path.
 */
function resolveLinetypeName(
  raw: string | undefined,
  blockLinetype: string | null = null,
): string {
  if (raw === undefined || raw === null) return "ByLayer";
  const trimmed = String(raw).trim();
  if (trimmed === "") return "ByLayer";
  if (trimmed.toUpperCase() === "BYBLOCK") {
    // Inherit the parent INSERT's resolved linetype; degrade to Continuous
    // at root scope (the same fallback the BYBLOCK colour chain uses).
    return blockLinetype && blockLinetype.trim() !== ""
      ? blockLinetype.trim()
      : "Continuous";
  }
  return trimmed;
}

/**
 * Parse a lineweight string from the entity's property bag. Matches
 * the DXF group-code 370 convention used by AutoCAD: numeric values
 * are in 0.01 mm units. We convert into millimetres so the
 * `RenderableEntity.lineweightWorld` field carries a uniform unit.
 *
 * Returns `0` for `BYLAYER`, `BYBLOCK`, `Default`, missing, or any
 * non-numeric input. The renderer treats `0` as "use the layer
 * default" (Q19.C).
 */
function parseLineweight(raw: string | undefined): number {
  if (raw === undefined || raw === null) return 0;
  const trimmed = String(raw).trim();
  if (
    trimmed === "" ||
    trimmed.toUpperCase() === "BYLAYER" ||
    trimmed.toUpperCase() === "BYBLOCK" ||
    trimmed.toUpperCase() === "DEFAULT"
  ) {
    return 0;
  }
  const parsed = parseFloat(trimmed);
  if (!Number.isFinite(parsed)) return 0;
  // Heuristic: AutoCAD's group-code 370 ranges from 0..211 in
  // 0.01-mm units (i.e. up to 2.11 mm). Values bigger than that are
  // already in mm. This matches what the existing `renderSimpleEntities`
  // code (line 79) does with the layer.lineweight field.
  return parsed > 5 ? parsed * 0.01 : parsed;
}

/** Helper used by tests: collapse a Vec3 list bbox into the `bounds` shape. */
export function boundsFromPoints(
  points: Iterable<Vec3>,
): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (!Number.isFinite(minX)) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  }
  return { minX, minY, maxX, maxY };
}
